import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import IntegrationActions from '../IntegrationActions';
import { getJson } from '../helpers';
import BudgetSyncModal from './BudgetSyncModal';
import ContractSyncModal from './ContractSyncModal';
import InvoiceSyncModal from './InvoiceSyncModal';
import OwnerBillingSyncModal from './OwnerBillingSyncModal';
import LaborCostSyncModal from './LaborCostSyncModal';

export default function ClaretProjectToolEmbed({
  title,
  description,
  projectVuid,
  recordQueryKey,
  recordVuid,
  createLabel,
  retrieveKind,
  sendKind,
  sendPayload,
  syncKind,
  hideChrome,
  children,
}) {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projectName, setProjectName] = useState('');
  const [syncOpen, setSyncOpen] = useState(false);
  const [embedKey, setEmbedKey] = useState(0);
  const embedRef = useRef(null);
  const projectParam = searchParams.get('project');
  const recordParam = recordQueryKey ? searchParams.get(recordQueryKey) : null;
  const resolvedRecordVuid = recordVuid || params.recordVuid;

  useEffect(() => {
    if (!projectVuid) return undefined;
    let cancelled = false;
    getJson(`/api/projects/${projectVuid}`)
      .then((project) => {
        if (!cancelled && project?.project_name) setProjectName(project.project_name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  useEffect(() => {
    const next = new URLSearchParams();
    searchParams.forEach((value, key) => next.set(key, value));
    let changed = false;
    if (projectVuid && projectParam !== projectVuid) {
      next.set('project', projectVuid);
      changed = true;
    }
    if (recordQueryKey && resolvedRecordVuid && recordParam !== resolvedRecordVuid) {
      next.set(recordQueryKey, resolvedRecordVuid);
      changed = true;
    }
    if (changed) setSearchParams(next, { replace: true });
  }, [projectVuid, recordQueryKey, resolvedRecordVuid, projectParam, recordParam, searchParams, setSearchParams]);

  const openCreate = () => {
    const embeddedCreate = embedRef.current?.querySelector('[data-pc-tool-create]');
    if (embeddedCreate) {
      embeddedCreate.click();
      return;
    }
    const next = new URLSearchParams(searchParams);
    if (projectVuid) next.set('project', projectVuid);
    next.set('create', 'true');
    setSearchParams(next);
  };

  const embed = (
    <div className="pc-je-embed pc-tool-embed" ref={embedRef} key={embedKey}>
      {children}
    </div>
  );
  const showSyncButton =
    syncKind === 'budget' ||
    syncKind === 'contract' ||
    syncKind === 'invoice' ||
    syncKind === 'owner-billing' ||
    syncKind === 'labor-cost';
  const closeSync = () => setSyncOpen(false);
  const completeSync = () => {
    setSyncOpen(false);
    setEmbedKey((value) => value + 1);
  };
  const syncModal =
    syncKind === 'budget' && syncOpen ? (
      <BudgetSyncModal projectVuid={projectVuid} onClose={closeSync} onComplete={completeSync} />
    ) : syncKind === 'contract' && syncOpen ? (
      <ContractSyncModal projectVuid={projectVuid} onClose={closeSync} onComplete={completeSync} />
    ) : syncKind === 'invoice' && syncOpen ? (
      <InvoiceSyncModal projectVuid={projectVuid} onClose={closeSync} onComplete={completeSync} />
    ) : syncKind === 'owner-billing' && syncOpen ? (
      <OwnerBillingSyncModal projectVuid={projectVuid} onClose={closeSync} onComplete={completeSync} />
    ) : syncKind === 'labor-cost' && syncOpen ? (
      <LaborCostSyncModal projectVuid={projectVuid} onClose={closeSync} onComplete={completeSync} />
    ) : null;

  if (hideChrome) {
    return (
      <>
        {createLabel || showSyncButton ? (
          <div className="pc-title-row">
            <span />
            <div className="pc-title-actions">
              {createLabel ? (
                <button type="button" className="pc-btn" onClick={openCreate}>
                  {createLabel}
                </button>
              ) : null}
              {showSyncButton ? (
                <button type="button" className="pc-btn pc-btn-secondary" onClick={() => setSyncOpen(true)}>
                  Sync
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
        {embed}
        {syncModal}
      </>
    );
  }

  return (
    <div className="pc-page">
      <div className="pc-crumb">
        <Link to="/procore">Portfolio</Link>
        {' / '}
        <Link to={`/procore/projects/${projectVuid}`}>{projectName || 'Project'}</Link>
        {' / '}
        {title}
      </div>
      <div className="pc-title-row">
        <h1>{title}</h1>
        <div className="pc-title-actions">
          {createLabel ? (
            <button type="button" className="pc-btn" onClick={openCreate}>
              {createLabel}
            </button>
          ) : null}
          {showSyncButton ? (
            <button type="button" className="pc-btn pc-btn-secondary" onClick={() => setSyncOpen(true)}>
              Sync
            </button>
          ) : (
            <IntegrationActions
              projectVuid={projectVuid}
              retrieveKind={retrieveKind}
              sendKind={sendKind}
              sendPayload={sendPayload}
            />
          )}
        </div>
      </div>
      {embed}
      {syncModal}
    </div>
  );
}
